import { Maybe } from 'types/types.generated';
import { FeatureToggleName, IFeatureToggles } from './featureToggles.types';
import { featureTogglesDefault } from './envs/default';
import { featureTogglesDev } from './envs/dev';
import { featureTogglesEngprod } from './envs/engprod';
import { featureTogglesMock } from './envs/mock';
import { featureTogglesQA } from './envs/qa';
import { featureTogglesUat } from './envs/uat';
import { AppEnv } from '../appConfig/appConfig.types';

/**
 * Create Feature Toggle Config Object
 * Get hard-coded feature flags based on the current environment.
 * @param appEnv current app environment
 *
 * @see https://chalk.charter.com/display/ERSPM/FE+Configs.
 */
export const getLocalFeatureTogglesByEnv = (
  appEnv: AppEnv
): IFeatureToggles => {
  switch (appEnv) {
    case AppEnv.Mock:
      return featureTogglesMock;

    case AppEnv.Dev:
      return featureTogglesDev;

    case AppEnv.QA:
      return featureTogglesQA;

    case AppEnv.Uat:
      return featureTogglesUat;

    case AppEnv.EngProd:
      return featureTogglesEngprod;

    /**
     * Prod, Prod Inactive and Stage intentionally fall through to
     * featureTogglesDefault, which is the production toggle set. Only add a case
     * here for an env that must deviate from production.
     */
    default:
      return featureTogglesDefault;
  }
};

/**
 * Get the FeatureToggleName enum buy toggle name
 * @param toggleName toggle name
 * @returns enum value of the toggle name
 */
export const getFeatureTogglesByName = (
  toggleName: string
): Maybe<FeatureToggleName> => {
  return FeatureToggleName[toggleName as keyof typeof FeatureToggleName];
};

/**
 * Check whether a feature toggle is enabled
 *
 * @param toggleNameToCheck a set of feature toggles to check
 * @param availableTogglesMap the set of all the feature toggles available for this env
 * @param isProd Whether we are in Prod or not
 */
export const isFeatureToggleEnabled = (
  toggleNameToCheck: FeatureToggleName,
  availableTogglesMap: Map<FeatureToggleName, boolean>,
  isProd: boolean
): boolean => {
  // Before checking Feature Toggles Map, return sessionStorage value if it has been updated manually.
  // DEMO is considered a Prod Env, so DEMO will not be able to use SessionStorage for updating values on the fly.
  if (!isProd) {
    return sessionStorage.getItem(`TOGGLE_${toggleNameToCheck}`) === 'true';
  }

  // If no Feature Toggles available, return false
  if (!availableTogglesMap || availableTogglesMap.size === 0) {
    return false;
  }

  // If Toggle Name is not found, default to false.
  return availableTogglesMap.get(toggleNameToCheck) ?? false;
};

/**
 * PRocess the query and respect the @toggle directive
 * @param document The query to process
 * @param isFeatureToggledOn Function to check if a feature toggle is on
 * @returns the processed query
 */
export const featureTogglesInGql = (
  query: string,
  isFeatureToggledOn: (toggle: FeatureToggleName) => boolean
): string => {
  // Regular expression to match fields with the @toggleField directive
  const toggleRegex = /(\w+)\s+@toggleField\(if:\s*"(\w+\.\w+)"\)/g;

  // Replace function to handle the name and toggle condition
  const fieldTogglesDoc = query.replace(
    toggleRegex,
    (match, fieldName: string, fullToggle: string) => {
      const toggleEnum = getFeatureTogglesByName(fullToggle.split('.')[1]);
      if (!toggleEnum) {
        return '';
      }
      return isFeatureToggledOn(toggleEnum) ? fieldName : '';
    }
  );

  // Regular expression to match blocks with the @toggleBlock directive
  const toggleBlockRegex =
    /(\w+|\.\.\.\s+on\s+\w+)\s+@toggleBlock\(if:\s*"(\w+\.\w+)"\)\s*\{((?:[^{}]*|\{(?:[^{}]*|\{[^{}]*\})*\})*)\}/g;

  // Replace function to handle the block and toggle condition for @toggleBlock
  return fieldTogglesDoc.replace(
    toggleBlockRegex,
    (match, blockName: string, fullToggle: string, blockContent: string) => {
      const toggleEnum = getFeatureTogglesByName(fullToggle.split('.')[1]);
      if (!toggleEnum) {
        return '';
      }
      return isFeatureToggledOn(toggleEnum)
        ? `${blockName} {${blockContent}}`
        : '';
    }
  );
};
